import { without } from 'lodash';
import {
  arrayOptional,
  enumOptional,
  enumRequired,
  guidOptional,
  labeled,
  stringRequired,
  stringOptional,
  numberOptional,
  timeStampEpochRequired,
  timeStampEpochOptional
} from '../../util/joi';
import { contactEmploymentBases } from './ContactEmploymentBasis.enum';
import { schema as contactPersonDependentSchema } from './ContactPersonDependent.schema';
import { contactMaritalStatusOptions } from './ContactMaritalStatusOption.enum';
import {
  ContactDriverLicenceType,
  contactDriverLicenceTypes
} from './ContactDriverLicenceType.enum';
import { ContactGender, contactGenders } from './ContactGender.enum';
import {
  ContactResidencyStatusOption,
  contactResidencyStatusOptions
} from './ContactResidencyStatusOption.enum';
import { PersonalTitle, personalTitles } from '../global/PersonalTitle.enum';

export const schema = (Joi: any, label: string) => {
  const availablePersonalTitles = without(personalTitles, PersonalTitle.none.name);
  const availableContactGenders = without(contactGenders, ContactGender.none.name);
  const availableContactResidencyStatusOptions = without(
    contactResidencyStatusOptions,
    ContactResidencyStatusOption.none.name
  );
  const availableContactDriverLicenceTypes = without(
    contactDriverLicenceTypes,
    ContactDriverLicenceType.none.name
  );
  return Joi.object()
    .keys({
      title: enumRequired(Joi, availablePersonalTitles, labeled(label, 'Title')),
      firstName: stringRequired(Joi, 1, 64).label(labeled(label, 'First name')),
      middleName: stringOptional(Joi, 1, 64).label(labeled(label, 'Middle name')),
      familyName: stringRequired(Joi, 1, 64).label(labeled(label, 'Family name')),
      preferredName: stringOptional(Joi, 1, 64).label(labeled(label, 'Preferred name')),
      previousName: stringOptional(Joi, 1, 128).label(labeled(label, 'Previous name')),
      dateOfBirth: timeStampEpochRequired(Joi).label(labeled(label, 'Date of birth')),
      gender: enumRequired(Joi, availableContactGenders, labeled(label, 'Gender')),
      countryOfResidency: stringRequired(Joi, 1, 64).label(
        labeled(label, 'Country of residency')
      ),
      countryOfTaxResidence: stringRequired(Joi, 1, 64).label(
        labeled(label, 'Country of tax residency')
      ),
      citizenship: stringRequired(Joi, 1, 64).label(labeled(label, 'Citizenship')),
      residentialStatus: enumRequired(
        Joi,
        availableContactResidencyStatusOptions,
        labeled(label, 'Residential status')
      ),
      countryOfBirth: stringRequired(Joi, 1, 64).label(labeled(label, 'Country of birth')),
      driversLicenseNumber: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(labeled(label, 'Drivers license - Number')),
        otherwise: stringRequired(Joi, 1, 64).label(labeled(label, 'Drivers license - Number'))
      }),
      driversLicenseCardNumber: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(labeled(label, 'Drivers license - Card number')),
        otherwise: stringRequired(Joi, 1, 64).label(labeled(label, 'Drivers license - Card number'))
      }),
      driversLicenseNameOnDocument: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(
          labeled(label, 'Drivers license - Name on document')
        ),
        otherwise: stringRequired(Joi, 1, 64).label(
          labeled(label, 'Drivers license - Name on document')
        )
      }),
      driversLicenseType: Joi.any().when('$isAssetFinance', {
        is: true,
        then: enumOptional(Joi, [ContactDriverLicenceType.available()[0].name]).label(
          labeled(label, 'Drivers license - Type')
        ),
        otherwise: enumRequired(
          Joi,
          availableContactDriverLicenceTypes,
          labeled(label, 'Drivers license - Type')
        )
      }),
      driversLicenseStateOfIssue: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(
          labeled(label, 'Drivers license - State of issue')
        ),
        otherwise: stringRequired(Joi, 1, 64).label(
          labeled(label, 'Drivers license - State of issue')
        )
      }),
      driversLicenseExpiryDate: Joi.any().when('$isAssetFinance', {
        is: true,
        then: timeStampEpochOptional(Joi).label(
          labeled(label, 'Drivers license - Expiry date')
        ),
        otherwise: timeStampEpochRequired(Joi).label(
          labeled(label, 'Drivers license - Expiry date')
        )
      }),
      passportNumber: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(labeled(label, 'Passport - Number')),
        otherwise: stringRequired(Joi, 1, 64).label(labeled(label, 'Passport - Number'))
      }),
      passportNameOnDocument: Joi.any().when('$isAssetFinance', {
        is: true,
        then: Joi.any(),
        otherwise: stringRequired(Joi, 1, 64).label(
          labeled(label, 'Passport - Name on document')
        )
      }),
      passportIssueCountry: Joi.any().when('$isAssetFinance', {
        is: true,
        then: stringOptional(Joi, 1, 64).label(labeled(label, 'Passport - Country of issue')),
        otherwise: stringRequired(Joi, 1, 64).label(
          labeled(label, 'Passport - Country of issue')
        )
      }),
      passportIssueDate: Joi.any().when('$isAssetFinance', {
        is: true,
        then: Joi.any(),
        otherwise: Joi.number()
          .required()
          .not(0)
          .label(labeled(label, 'Passport - Issue date'))
      }),
      passportExpiryDate: Joi.any().when('$isAssetFinance', {
        is: true,
        then: Joi.any(),
        otherwise: Joi.number()
          .required()
          .not(0)
          .label(labeled(label, 'Passport - Expiry date'))
      }),
      maritalStatus: enumOptional(
        Joi,
        contactMaritalStatusOptions,
        labeled(label, 'Marital status')
      ),
      numberOfDependents: numberOptional(Joi).label(labeled(label, 'Number of dependents')),
      dependents: arrayOptional(
        Joi,
        contactPersonDependentSchema(Joi, labeled(label, 'Dependent -'))
      ),
      idSpouse: guidOptional(Joi).label(labeled(label, 'Spouse')),
      spouseFirstName: stringOptional(Joi, 1, 64).label(labeled(label, 'Spouse - First name')),
      spouseFamilyName: stringOptional(Joi, 1, 64).label(labeled(label, 'Spouse - Family name'))
    })
    .when('.maritalStatus.name', {
      switch: [
        {
          is: 'Married',
          then: Joi.any().when('$isAssetFinance', {
            is: true,
            then: Joi.any(),
            otherwise: Joi.object({
              spouseFirstName: stringRequired(Joi, 1, 64).label(
                labeled(label, 'Spouse - First name')
              ),
              spouseFamilyName: stringRequired(Joi, 1, 64).label(
                labeled(label, 'Spouse - Family name')
              )
            })
          })
        },
        {
          is: 'DeFacto',
          then: Joi.any().when('$isAssetFinance', {
            is: true,
            then: Joi.any(),
            otherwise: Joi.object({
              spouseFirstName: stringRequired(Joi, 1, 64).label(
                labeled(label, 'Spouse - First name')
              ),
              spouseFamilyName: stringRequired(Joi, 1, 64).label(
                labeled(label, 'Spouse - Family name')
              )
            })
          })
        }
      ]
    });
};

// .when('.driversLicenseStateOfIssue', {
//     is: Joi.valid('vic', 'qld'),
//     then: {
//       driversLicenseCardNumber: stringOptional(Joi, 1, 64).label(labeled(label, 'Drivers license - Card number'))
//     },
//     otherwise: {
//       driversLicenseCardNumber: stringRequired(Joi, 1, 64).label(labeled(label, 'Drivers license - Card number'))
//     }
//   })


export default {
  schema
};
